import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaStar, FaSpinner } from 'react-icons/fa';
import { UnifiedSpotlightModal } from './UnifiedSpotlightModal';
import { SpotlightSuccessModal } from './SpotlightSuccessModal';
import { useProfileSpotlight } from '../hooks/useProfileSpotlight';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { fetchUserBalance } from '@/redux/thunks/marketplaceThunks';
import { toast } from 'react-toastify';
import { formatName } from '@/utils/formatName';
import './PostSpotlightButton.scss'; // Reuse same styles

interface ProfileSpotlightButtonProps {
  profileId: string;
  profileUsername: string;
  /** Optional — when present, the success modal greets the creator
   *  by display name. Falls back to @username when not set. */
  profileDisplayName?: string;
  onSuccess?: () => void;
  durationHours?: number; // Default duration
  className?: string;
  /** 'location' (default; existing marketplace behavior — pins to
   *  the profile owner's state) or 'global' (surfaces on the
   *  /agents/discover Spotlighted rail; used when spotlighting
   *  agents). Modal copy adjusts based on scope. */
  audienceScope?: 'location' | 'global';
}

/**
 * Reusable button component for spotlighting profiles
 * Streamlined flow: click -> select duration & see price -> confirm
 */
export const ProfileSpotlightButton = React.forwardRef<HTMLButtonElement, ProfileSpotlightButtonProps>(({
  profileId,
  profileUsername,
  profileDisplayName,
  onSuccess,
  durationHours = 24, // Default 24 hours
  className = '',
  audienceScope = 'location',
}, ref) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const isEmailUser = user?.authMethod === 'email';

  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedDuration, setCompletedDuration] = useState(24);

  const {
    prepareTransaction,
    executeTransaction,
    recordPurchase,
    purchaseSpotlight,
    reset,
  } = useProfileSpotlight();

  /**
   * User clicks spotlight button
   */
  const handleSpotlightClick = () => {
    setShowModal(true);
  };

  /**
   * Load price when duration changes
   */
  const handleDurationChange = async (duration: number): Promise<{
    price: number;
    canProceed?: boolean;
    warning?: string;
    balance?: { current: number; required: string; shortfall: string | null };
  }> => {
    try {
      const details = await prepareTransaction({ profileId, durationHours: duration, audienceScope });
      return {
        price: parseFloat(details.totalCost) || 0,
        canProceed: details.canProceed,
        warning: details.warning,
        balance: details.balance,
      };
    } catch (err: any) {
      console.error('Failed to get price:', err);
      throw err;
    }
  };

  /**
   * User confirms spotlight purchase
   */
  const handleConfirm = async (duration: number) => {
    setIsProcessing(true);
    setCompletedDuration(duration);

    try {
      if (isEmailUser) {
        // Email users: Backend signs and executes transaction
        toast.info('Processing spotlight purchase...');
        await purchaseSpotlight({ profileId, durationHours: duration, audienceScope });
      } else {
        // Wallet users: Sign with Klever Web Extension
        toast.info('Please sign the transaction in your Klever wallet...');
        const txHash = await executeTransaction();

        // Record purchase in database
        toast.info('Recording purchase...');
        await recordPurchase(txHash);
      }

      // Refresh user balance — spotlight spend is debited on chain but no
      // thunk in the spotlight flow refetches the displayed balance.
      dispatch(fetchUserBalance());

      // Close spotlight modal and show success modal
      setShowModal(false);
      setShowSuccessModal(true);
      reset();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to spotlight profile');
      console.error('Spotlight error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * User cancels
   */
  const handleCancel = () => {
    setShowModal(false);
    reset();
  };

  return (
    <>
      <button
        ref={ref}
        className={`spotlight-btn ${className}`}
        onClick={handleSpotlightClick}
        title="Spotlight this profile"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <FaSpinner className="spinner" /> <span className="btn-label">Processing...</span>
          </>
        ) : (
          <>
            <FaStar /> <span className="btn-label">Spotlight</span>
          </>
        )}
      </button>

      {/* Unified Spotlight Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <UnifiedSpotlightModal
          isOpen={showModal}
          title="Spotlight Profile"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          onDurationChange={handleDurationChange}
          isProcessing={isProcessing}
          defaultDuration={durationHours}
          explainer={
            audienceScope === 'global'
              ? 'Global spotlights appear at the top of the /agents/discover page for every viewer — no location filter — for the duration you select. Slot cap is 10 concurrent globally. After the timer runs out the placement clears automatically.'
              : 'Spotlighted profiles appear pinned at the top of the marketplace for anyone in the same state as the profile owner — for the duration you select. After the timer runs out the badge clears automatically.'
          }
        />,
        document.body
      )}

      {/* Success Modal - Rendered via Portal. Reuses formatName so the
          identity convention (#248) matches the marketplace card and
          every other surface that renders a user. */}
      {ReactDOM.createPortal(
        <SpotlightSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title="Profile Spotlighted!"
          message={`${formatName({ displayName: profileDisplayName, username: profileUsername })} is now featured for ${completedDuration} hours! 🎉`}
          duration={3000}
        />,
        document.body
      )}
    </>
  );
});

ProfileSpotlightButton.displayName = 'ProfileSpotlightButton';

export default ProfileSpotlightButton;
